import { DatasetOrigin, TaskType } from '@/shared/domain/taskTypes';
import { stripCloneSuffix } from '@/features/datasets/utils/datasetCloneNames';

/** Dropdown value for "LLM created" — persisted origin stays `specific_ai_fully_generated`. */
export const LLM_CREATED_DATA_CREATION = 'commercial_model';

export const AI_GENERATED_EXAMPLE_COLUMN = 'example';

const USER_SELECTABLE_DATA_CREATION = new Set(['human_created', 'commercial_model', 'other']);

const AI_ORIGINS = new Set([
    DatasetOrigin.specific_ai_fully_generated,
    DatasetOrigin.specific_ai_labeled,
]);

export function isAiGeneratedOrigin(dataCreationType) {
    return AI_ORIGINS.has(dataCreationType);
}

/** Matches `{stem}_ai` (session 1) or `{stem}_ai{n}` with n >= 2. `_ai1` is unused. */
const AI_SESSION_RE = /^(.+)_ai(?:([2-9]|[1-9]\d+))?$/;
const MAX_AI_GENERATED_DATASET_NAME_LENGTH = 40;
const AI_DATASET_TILE_MAX_CHARS = 22;

/**
 * Parse a Generate-with-AI session file name after clone-suffix strip.
 * @returns {{ stem: string, index: number, stripped: string } | null}
 */
export function parseAiGeneratedDatasetName(datasetName) {
    if (!datasetName || typeof datasetName !== 'string') return null;
    const stripped = stripCloneSuffix(datasetName) || datasetName;
    const match = stripped.match(AI_SESSION_RE);
    if (!match) return null;
    return {
        stem: match[1],
        index: match[2] ? Number(match[2]) : 1,
        stripped,
    };
}

export function formatAiGeneratedDatasetName(
    usecaseName,
    index,
    maxLength = MAX_AI_GENERATED_DATASET_NAME_LENGTH,
) {
    const suffix = index <= 1 ? '_ai' : `_ai${index}`;
    const stemSource = usecaseName && typeof usecaseName === 'string' ? usecaseName : 'usecase';
    const maxStem = Math.max(1, maxLength - suffix.length);
    return `${stemSource.slice(0, maxStem)}${suffix}`;
}

/**
 * Generate-with-AI attaches `{usecase_name}_ai`, then `{usecase_name}_ai2`, …
 * After clone-suffix strip, session files match that pattern (not only `_ai`).
 */
export function isAiGeneratedDatasetName(datasetName, usecaseName) {
    const parsed = parseAiGeneratedDatasetName(datasetName);
    if (!parsed) return false;
    if (!usecaseName) return true;
    return formatAiGeneratedDatasetName(usecaseName, parsed.index) === parsed.stripped;
}

/** First free `{task}_ai` / `{task}_ai2` / `{task}_ai{n}` not in `existingNames`. */
export function nextAiGeneratedDatasetName(usecaseName, existingNames = []) {
    const taken = new Set(
        (existingNames || []).map((name) => stripCloneSuffix(name) || name).filter(Boolean)
    );
    let index = 1;
    while (index < 10000) {
        const candidate = formatAiGeneratedDatasetName(usecaseName, index);
        if (!taken.has(candidate)) return candidate;
        index += 1;
    }
    throw new Error('Could not allocate an AI-generated dataset name');
}

/**
 * Keep the `_ai` / `_ai2` suffix visible when the tile truncates a long name.
 */
export function displayAiGeneratedDatasetName(
    datasetName,
    maxChars = AI_DATASET_TILE_MAX_CHARS,
) {
    const parsed = parseAiGeneratedDatasetName(datasetName);
    if (!parsed) {
        return typeof datasetName === 'string' ? datasetName : '';
    }
    if (parsed.stripped.length <= maxChars) return parsed.stripped;
    const suffix = parsed.index <= 1 ? '_ai' : `_ai${parsed.index}`;
    const budget = Math.max(4, maxChars - suffix.length - 1);
    return `${parsed.stem.slice(0, budget)}…${suffix}`;
}

export function isAiGeneratedDataset({ dataCreationType, datasetName, usecaseName } = {}) {
    return (
        isAiGeneratedOrigin(dataCreationType) ||
        isAiGeneratedDatasetName(datasetName, usecaseName)
    );
}

/** Upload-slot entry created by Generate with AI (hydrated `{usecase}_ai` chip). */
export function isAiGeneratedUploadEntry(entry, usecaseName) {
    if (!entry) return false;
    return isAiGeneratedDataset({
        dataCreationType: entry.dataCreationType,
        datasetName: entry.datasetName || entry.uploadName,
        usecaseName,
    });
}

/**
 * Classification labels / NER entity types Generate-with-AI writes for every
 * row. Same catalog as Business Goals and the generation modal — not scanned
 * from a file that does not exist yet.
 */
export function knownLabelsForAiGeneration({ formLabels, usecase, taskType } = {}) {
    const type = taskType || usecase?.task_type;
    if (
        type !== TaskType.classification &&
        type !== TaskType.NER &&
        type !== TaskType.NER_WITH_RELATIONS
    ) {
        return [];
    }
    const isNer = type === TaskType.NER || type === TaskType.NER_WITH_RELATIONS;
    const counts = isNer ? usecase?.entity_names_count : usecase?.label_names_count;
    const manual = isNer ? usecase?.manually_added_entities : usecase?.manually_added_labels;
    return [
        ...new Set(
            [...(formLabels || []), ...Object.keys(counts || {}), ...(manual || [])]
                .map((name) => (name == null ? '' : String(name).trim()))
                .filter(Boolean)
        ),
    ];
}

/** Identity map so Review Extracted Labels can render without a file scan. */
export function identityLabelMappings(labels) {
    return Object.fromEntries((labels || []).map((label) => [label, label]));
}

/** Response / labels column the wizard dropdowns expect, per task type. */
export function defaultResponseColumnForTask(taskType) {
    if (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) {
        return 'entities';
    }
    if (taskType === TaskType.summarization) return 'summary';
    if (taskType === TaskType.contentGeneration) return 'output';
    return 'labels';
}

export function canonicalAiGeneratedColumns(taskType) {
    return [AI_GENERATED_EXAMPLE_COLUMN, defaultResponseColumnForTask(taskType)];
}

export function canonicalAiGeneratedMappings(taskType) {
    return {
        example_column_name: AI_GENERATED_EXAMPLE_COLUMN,
        label_column_name: defaultResponseColumnForTask(taskType),
    };
}

/** Map persisted origin onto a value the "Data was created by" dropdown can show. */
export function dataCreationForUi(dataCreationType) {
    if (USER_SELECTABLE_DATA_CREATION.has(dataCreationType)) return dataCreationType;
    if (isAiGeneratedOrigin(dataCreationType)) return LLM_CREATED_DATA_CREATION;
    return null;
}

/**
 * When an AI-generated dataset has no file columns / mappings (ProcessData
 * ingest writes rows, not file metadata), fill the canonical example +
 * labels/entities/summary/output names so the Data Collection dropdowns
 * have something to select.
 */
export function applyAiGeneratedColumnDefaults({
    columns = [],
    mappings = {},
    taskType,
    dataCreationType,
    datasetName,
    usecaseName,
} = {}) {
    if (
        !isAiGeneratedDataset({
            dataCreationType,
            datasetName,
            usecaseName,
        })
    ) {
        return { columns: [...columns], mappings: { ...mappings } };
    }
    const nextColumns = [...columns];
    const seen = new Set(nextColumns);
    for (const name of canonicalAiGeneratedColumns(taskType)) {
        if (!seen.has(name)) {
            seen.add(name);
            nextColumns.push(name);
        }
    }
    const nextMappings = { ...mappings };
    if (!nextMappings.example_column_name) {
        nextMappings.example_column_name = AI_GENERATED_EXAMPLE_COLUMN;
    }
    if (!nextMappings.label_column_name) {
        nextMappings.label_column_name = defaultResponseColumnForTask(taskType);
    }
    return { columns: nextColumns, mappings: nextMappings };
}
